import { EventEmitter } from '@angular/core';
import { Observable } from 'rxjs';
import { LocalStorage } from '../../storage.class';
import { Utils } from '../../utils.class';
import { WebsocketService } from '../websocket/websocket.service';
import { RealtimeInterface } from './realtime-interface';
import { RealtimeType } from './realtime-type.enum';

export abstract class RealtimeService<T extends RealtimeInterface> {
    websocketOpen = new EventEmitter<boolean>();

    private items: T[] = [];
    protected requestedDates = new Map<string, boolean>();
    private requestedIds = new Map<string | number, boolean>();
    private itemChanged = new EventEmitter<(number | string)[]>();
    private retryTimeoutPointer: ReturnType<typeof setTimeout>;
    protected listOnlyFilteredFrontend = false;
    private init = false;
    private wasDisconnected = false;

    private requestedDateKey(date: number, filterId?: any): string {
        return filterId != null ? `${filterId}_${date}` : `${date}`;
    }

    protected constructor(
        protected websocket: WebsocketService,
        protected type: RealtimeType,
        protected identifier = 'id'
    ) {
        this.websocketInit();

        this.websocket.websocketOpen.subscribe(isOpen => {
            this.websocketOpen.emit(isOpen);

            if (isOpen) {
                if (this.wasDisconnected) {
                    this.itemChanged.emit([]);
                }
            } else {
                this.wasDisconnected = true;

                this.retryWebsocketInit();
            }
        });
    }

    private websocketInit() {
        if (this.init || !LocalStorage.getUserToken()) {
            return;
        }

        this.init = true;

        this.websocket.getWebsocket<T[]>(this.type).subscribe({
            next: (items: { data: T[]; reinit: boolean }) => {
                if (items?.data && Array.isArray(items?.data)) {
                    this.items = this.items ?? [];

                    items.data
                        .filter(i => !!i)
                        .forEach(item => {
                            const currentItem = this.items.find(p => +p[this.identifier] === +item[this.identifier]);

                            if (item['deleted']) {
                                if (currentItem) {
                                    this.items.splice(this.items.indexOf(currentItem), 1);
                                }
                            } else if (currentItem) {
                                Object.assign(currentItem, item);
                            } else {
                                this.items.push(item);
                            }
                        });

                    this.itemChanged.emit(
                        items.data
                            .filter(i => !!i)
                            .map(i => (isNaN(+i[this.identifier]) ? i[this.identifier] : +i[this.identifier]))
                    );
                } else if (!!items?.reinit) {
                    this.retryWebsocketInit();
                }
            },
            error: () => this.retryWebsocketInit(),
            complete: () => this.retryWebsocketInit(),
        });
    }

    private retryWebsocketInit() {
        this.init = false;

        if (this.retryTimeoutPointer) {
            clearTimeout(this.retryTimeoutPointer);
        }

        this.retryTimeoutPointer = setTimeout(() => {
            this.websocketInit();
        }, 1000);
    }

    public getFilteredList(fromDate: Date, toDate: Date, filterId?: any): Observable<T[]> {
        this.websocketInit();

        let newFromDate = Utils.setTime(new Date(fromDate), 0, 0);
        let newToDate = Utils.setTime(new Date(toDate), 0, 0);

        if (toDate.getTime() > newToDate.getTime()) {
            newToDate.setDate(newToDate.getDate() + 1);
        }

        return new Observable<T[]>(observer => {
            let loopDate = new Date(newFromDate);
            let allRequested = true;

            while (loopDate.getTime() < newToDate.getTime()) {
                if (!this.requestedDates.has(this.requestedDateKey(loopDate.getTime(), filterId))) {
                    allRequested = false;
                }

                loopDate.setDate(loopDate.getDate() + 1);
            }

            const initOrFullList = () => {
                const loopDate = new Date(newFromDate);
                let newStart = null;
                let newEnd = null;
                while (loopDate.getTime() < newToDate.getTime()) {
                    if (!this.requestedDates.has(this.requestedDateKey(loopDate.getTime(), filterId))) {
                        if (!newStart) {
                            newStart = new Date(loopDate);
                        }
                        this.requestedDates.set(this.requestedDateKey(loopDate.getTime(), filterId), true);
                        // This date should be included, to newEnd = today + 1
                        newEnd = new Date(loopDate);
                        newEnd.setDate(newEnd.getDate() + 1);
                    }
                    loopDate.setDate(loopDate.getDate() + 1);
                }
                this.websocket.doRequest(this.type, newStart || newFromDate, newEnd, filterId);
            };

            if (allRequested || (this.requestedIds.has('list') && this.listOnlyFilteredFrontend)) {
                if (this.items) {
                    observer.next(this.items.filter(item => this.itemInDaterange(item, fromDate, toDate)));
                }
            } else {
                initOrFullList();
            }

            const itemChangedSubscription = this.itemChanged.subscribe(() => {
                if (this.wasDisconnected) {
                    this.requestedDates.clear();
                    this.wasDisconnected = false;
                    initOrFullList();
                }
                observer.next(this.items?.filter(item => this.itemInDaterange(item, fromDate, toDate)));
            });

            return {
                unsubscribe(): void {
                    itemChangedSubscription.unsubscribe();
                },
            };
        });
    }

    public getList(): Observable<T[]> {
        this.websocketInit();
        return new Observable<T[]>(observer => {
            const itemChangedSubscription = this.itemChanged.subscribe(() => {
                observer.next(this.items);
            });

            if (this.requestedIds.has('list')) {
                if (this.items) {
                    observer.next(this.items);
                }
            } else {
                this.websocket.doRequest(this.type);

                this.requestedIds.set('list', true);
            }

            return {
                unsubscribe(): void {
                    itemChangedSubscription.unsubscribe();
                },
            };
        });
    }

    public getLatestItem(): Observable<T> {
        this.websocketInit();
        return new Observable<T>(observer => {
            const itemChangedSubscription = this.itemChanged.subscribe(() => {
                const item = this.items.length > 0 ? this.items[this.items.length - 1] : null;
                observer.next(item);
            });

            if (this.requestedIds.has('latest')) {
                if (this.items) {
                    const item = this.items.length > 0 ? this.items[this.items.length - 1] : null;

                    if (item) {
                        observer.next(item);
                    }
                }
            } else {
                this.websocket.doRequest(this.type);

                this.requestedIds.set('latest', true);
            }

            return {
                unsubscribe(): void {
                    itemChangedSubscription.unsubscribe();
                },
            };
        });
    }

    public getSingle(id: number | string): Observable<T> {
        this.websocketInit();

        return new Observable<T>(observer => {
            const itemChangedSubscription = this.itemChanged.subscribe(ids => {
                if (ids.includes(id)) {
                    const item = this.items?.find(p => p[this.identifier] === id);

                    observer.next(item ?? ({ id, deleted: true, deleted_at: new Date() } as unknown as T));
                }
            });

            let item = null;

            if (this.items) {
                item = this.items.find(p => p[this.identifier] === id);
            }

            if (item) {
                observer.next(item);
            } else if (!this.requestedIds.has(id)) {
                this.websocket.doRequest(this.type, null, null, id);

                this.requestedIds.set(id, true);
            }

            return {
                unsubscribe(): void {
                    itemChangedSubscription.unsubscribe();
                },
            };
        });
    }

    public abstract itemInDaterange(planning: T, fromDate: Date, toDate: Date): boolean | void;

    protected getItems(): T[] {
        return this.items;
    }
}
